(() => {
  const loadVideo = video => {
    if (!video || video.dataset.loaded === 'true') return;
    video.querySelectorAll('source[data-src]').forEach(source => { source.src = source.dataset.src; });
    video.dataset.loaded = 'true';
    video.load();
    video.muted = true;
    video.defaultMuted = true;
    const rate = Number(video.dataset.hqpPlaybackRate);
    if (rate > 0) video.defaultPlaybackRate = video.playbackRate = rate;
  };
  const observeVisibility = (element, update) => {
    if (!('IntersectionObserver' in window)) { update(true); return; }
    const observer = new IntersectionObserver(([entry]) => update(entry.isIntersecting));
    observer.observe(element);
    document.addEventListener('shopify:section:unload', event => {
      if (event.target.contains(element)) observer.disconnect();
    }, { once: true });
  };
  const initializeVideo = video => {
    if (video.dataset.hqpInitialized) return;
    video.dataset.hqpInitialized = 'true';
    const toggle = video.parentElement.querySelector('[data-hqp-video-toggle]');
    const motion = matchMedia('(prefers-reduced-motion: reduce)');
    let paused = motion.matches;
    let visible = false;
    const updateControl = () => {
      if (!toggle) return;
      toggle.hidden = false;
      toggle.dataset.paused = String(video.paused);
      toggle.setAttribute('aria-label', video.paused ? toggle.dataset.playLabel : toggle.dataset.pauseLabel);
    };
    video.addEventListener('play', updateControl);
    video.addEventListener('pause', updateControl);
    const sync = () => {
      if (!paused && visible && !document.hidden) { loadVideo(video); video.play().catch(updateControl); }
      else video.pause();
      updateControl();
    };
    toggle?.addEventListener('click', () => { paused = !video.paused; sync(); });
    motion.addEventListener('change', () => { paused = motion.matches; sync(); });
    document.addEventListener('visibilitychange', sync);
    observeVisibility(video, value => { visible = value; sync(); });
    sync();
  };
  const initializeGallery = gallery => {
    if (gallery.dataset.hqpGalleryInitialized) return;
    gallery.dataset.hqpGalleryInitialized = 'true';
    const slides = [...gallery.querySelectorAll('[data-hqp-slide]')];
    const toggle = gallery.querySelector('[data-hqp-gallery-toggle]');
    const label = gallery.querySelector('[data-hqp-gallery-current]');
    const progress = gallery.querySelector('[data-hqp-gallery-progress]');
    const motion = matchMedia('(prefers-reduced-motion: reduce)');
    let paused = motion.matches;
    let visible = false;
    let hovering = false;
    let focused = false;
    let current = 0;
    let frame;
    let started = 0;
    const interval = 5000;
    const activeVideo = () => slides[current]?.querySelector('video');
    // Hover/focus can hold still images without preventing a video from playing.
    const running = () => !paused && visible && !document.hidden && (activeVideo() || (!hovering && !focused));
    const setProgress = value => progress?.style.setProperty('--hqp-gallery-progress', String(Math.max(0, Math.min(1, value))));
    const stop = () => { cancelAnimationFrame(frame); activeVideo()?.pause(); };
    const tick = now => {
      if (!running() || !gallery.isConnected) return;
      const video = activeVideo();
      if (video) setProgress(video.duration ? video.currentTime / video.duration : 0);
      else {
        const value = (now - started) / interval;
        setProgress(value);
        if (value >= 1) { show(current + 1); return; }
      }
      frame = requestAnimationFrame(tick);
    };
    const sync = () => {
      cancelAnimationFrame(frame);
      if (toggle) { toggle.hidden = false; toggle.dataset.paused = String(paused); toggle.setAttribute('aria-label', paused ? toggle.dataset.playLabel : toggle.dataset.pauseLabel); }
      if (!running()) { activeVideo()?.pause(); return; }
      const video = activeVideo();
      if (video) { loadVideo(video); if (video.paused) video.play().catch(() => { paused = true; sync(); }); }
      started = performance.now();
      frame = requestAnimationFrame(tick);
    };
    const show = index => {
      stop();
      current = (index + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        slide.hidden = i !== current;
        slide.setAttribute('aria-hidden', String(i !== current));
        const video = slide.querySelector('video');
        if (video) { video.pause(); if (video.readyState) video.currentTime = 0; }
      });
      if (label) label.textContent = String(current + 1);
      setProgress(0);
      sync();
    };
    gallery.querySelector('[data-hqp-gallery-previous]')?.addEventListener('click', () => show(current - 1));
    gallery.querySelector('[data-hqp-gallery-next]')?.addEventListener('click', () => show(current + 1));
    toggle?.addEventListener('click', () => {
      paused = !paused;
      // An explicit Play action takes precedence over the temporary interaction pause.
      if (!paused) { hovering = false; focused = false; }
      sync();
    });
    gallery.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') { hovering = true; sync(); } });
    gallery.addEventListener('pointerleave', event => { if (event.pointerType === 'mouse') { hovering = false; sync(); } });
    gallery.addEventListener('focusin', () => { focused = true; sync(); });
    gallery.addEventListener('focusout', event => { if (!gallery.contains(event.relatedTarget)) { focused = false; sync(); } });
    slides.forEach(slide => slide.querySelector('video')?.addEventListener('ended', () => { if (running()) show(current + 1); }));
    motion.addEventListener('change', () => { paused = motion.matches; sync(); });
    document.addEventListener('visibilitychange', sync);
    observeVisibility(gallery, value => {
      visible = value;
      // Buffer while the preceding photos are visible, before this slide is selected.
      if (visible && !motion.matches) gallery.querySelectorAll('[data-hqp-gallery-video]').forEach(loadVideo);
      sync();
    });
    show(0);
  };
  const initialize = (root = document) => {
    root.querySelectorAll('[data-hqp-loop-video]').forEach(initializeVideo);
    root.querySelectorAll('[data-hqp-gallery]').forEach(initializeGallery);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
})();
